import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Bolt as BoltIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Favorite as FavoriteIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material'
import { Room, RoomType, ROOM_TYPE_LABELS } from '@/types/profile'
import { getRooms, RoomResponse } from '@/services/roomService'
import { toRoomType } from '@/utils/roomType'

const mapRoomResponseToRoom = (response: RoomResponse): Room => ({
  id: response.roomId,
  name: response.roomName,
  type: toRoomType(response.categoryType),
  eventDate: response.eventDatetime ? new Date(response.eventDatetime) : undefined,
  expiresAt: response.expirationDate ? new Date(response.expirationDate) : undefined,
  maxParticipants: response.maxParticipants,
  createdAt: response.createdAt ? new Date(response.createdAt) : new Date(),
  participants: [],
})

const getRoomIcon = (type: RoomType) => {
  switch (type) {
    case 'business': return <BusinessIcon />
    case 'dating':   return <FavoriteIcon />
    case 'job':      return <WorkIcon />
  }
}

const getRoomColor = (type: RoomType) => {
  switch (type) {
    case 'business': return { bg: '#e0e7ff', color: '#6366f1' }
    case 'dating':   return { bg: '#fce7f3', color: '#ec4899' }
    case 'job':      return { bg: '#d1fae5', color: '#059669' }
  }
}

export const SubInfoEditPage = () => {
  const navigate = useNavigate()
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [roomParticipantCounts, setRoomParticipantCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoading(true)
        setError(null)
        const roomResponses = await getRooms()
        setRooms(roomResponses.map(mapRoomResponseToRoom))

        const counts: Record<string, number> = {}
        roomResponses.forEach((r) => {
          counts[r.roomId] = r.participantCount ?? 0
        })
        setRoomParticipantCounts(counts)
      } catch (fetchError) {
        console.error('Failed to fetch rooms:', fetchError)
        setError('ルーム一覧の取得に失敗しました')
      } finally {
        setLoading(false)
      }
    }

    fetchRooms()
  }, [])

  const renderRoomCard = (room: Room) => {
    const colors = getRoomColor(room.type)
    return (
      <Grid item xs={12} sm={6} md={4} key={room.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.2s ease-in-out',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
          }}
        >
          <CardContent sx={{ flex: 1, p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 44, height: 44, borderRadius: 2,
                  bgcolor: colors.bg, color: colors.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {getRoomIcon(room.type)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Chip
                  label={ROOM_TYPE_LABELS[room.type]}
                  size="small"
                  sx={{ bgcolor: colors.bg, color: colors.color, fontSize: '0.7rem', height: 22 }}
                />
              </Box>
            </Box>

            <Typography variant="h6" fontWeight={600} gutterBottom>
              {room.name}
            </Typography>

            {room.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {room.description}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              参加者: {roomParticipantCounts[room.id] ?? 0}人
            </Typography>
          </CardContent>

          <CardActions sx={{ p: { xs: 2, sm: 2.5 }, pt: 0 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<AssignmentIcon />}
              onClick={() => navigate(`/sub-info/edit/${room.id}`)}
              sx={{
                borderColor: colors.color,
                color: colors.color,
                '&:hover': { borderColor: colors.color, bgcolor: colors.bg },
              }}
            >
              サブ情報編集
            </Button>
          </CardActions>
        </Card>
      </Grid>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <BoltIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              サブ情報編集
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            サブ情報を編集するルームを選択してください
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : rooms.length === 0 ? (
          <Alert severity="info">表示できるルームがありません。</Alert>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {rooms.map(renderRoomCard)}
          </Grid>
        )}
      </Container>
    </Box>
  )
}
